import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LinkIcon from '@mui/icons-material/Link';
import { Box, Button, IconButton, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import { type ChangeEvent, type MouseEventHandler, useState } from 'react';
import RequiredAsterisk from '@/components/RequiredAsterisk';
import { truncate } from '@/utils';
import { errorSnackbar } from '@/utils/snackbar';

const TRIM_LENGTH = 50;

interface Props {
    label: string;
    value?: string | number | null;
    link?: string;
    isCopyable?: boolean;
    isEditable?: boolean;
    onChange?: (value: string) => void;
    onLinkClick?: MouseEventHandler<HTMLAnchorElement>;
    cantBeEmpty?: boolean;
    maxLength?: number;
    errorMessage?: string;
}

export default function EditableViewComponent({
    label,
    value,
    link,
    isCopyable,
    isEditable,
    onChange,
    onLinkClick,
    cantBeEmpty = false,
    maxLength,
    errorMessage,
}: Props) {
    const theme = useTheme();

    const [isEditing, setIsEditing] = useState(false);
    const [draftValue, setDraftValue] = useState('');
    const [showTrimmedText, setShowTrimmedText] = useState<boolean>(true);
    const [localError, setLocalError] = useState('');

    const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newValue = event.target.value;
        setDraftValue(newValue);

        if (maxLength && newValue.length > maxLength) {
            setLocalError(errorMessage || `${label} must be less than ${maxLength} characters.`);
        } else {
            setLocalError('');
        }
    };

    const startEditing = () => {
        setDraftValue(value?.toString() ?? '');
        setLocalError('');
        setIsEditing(true);
    };

    const handleUpdate = () => {
        if (cantBeEmpty && !draftValue) {
            errorSnackbar(`${label} cannot be empty!`);
            return;
        }
        onChange?.(draftValue);
        setIsEditing(false);
    };

    const handleDiscard = () => {
        setLocalError('');
        setIsEditing(false);
    };

    const handleTruncateText = () => {
        if (value && value?.toString()?.length > TRIM_LENGTH) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: showTrimmedText ? 'center' : 'flex-start',
                        gap: '8px',
                        flexDirection: showTrimmedText ? 'row' : 'column',
                    }}
                >
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 500,
                            lineHeight: '16px',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                        }}
                    >
                        {showTrimmedText ? truncate(value?.toString() ?? '', TRIM_LENGTH) : value}
                    </Typography>
                    <Button
                        variant="text"
                        sx={{
                            textTransform: 'none',
                            padding: showTrimmedText ? '0 4px' : '0px',
                            textAlign: 'left',
                            justifyContent: 'flex-start',
                        }}
                        onClick={() => setShowTrimmedText(!showTrimmedText)}
                    >
                        {showTrimmedText ? 'See more' : 'See less'}
                    </Button>
                </Box>
            );
        }

        return (
            <Typography variant="h4" sx={{ fontWeight: 500, lineHeight: '16px' }}>
                {value}
            </Typography>
        );
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${theme.palette.neutral.borderColor}`,
                paddingBottom: '10px',
                width: '100%',
                gap: '20px',
            }}
        >
            <Typography variant="body2" sx={{ width: '50%' }}>
                {cantBeEmpty ? <RequiredAsterisk /> : null}
                {label}
            </Typography>
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {link && !isEditing ? (
                    <Button
                        startIcon={<LinkIcon sx={{ fontSize: 14 }} />}
                        disableRipple={true}
                        onClick={onLinkClick}
                        href={link}
                        target="_blank"
                        variant="text"
                        sx={{
                            padding: '0px',
                            textTransform: 'none',
                            color: theme.palette.primary.main,
                            '& .MuiButton-startIcon > *:nth-of-type(1)': {
                                fontSize: 14,
                            },
                        }}
                    >
                        {value}
                    </Button>
                ) : null}
                {!link && !isEditing ? handleTruncateText() : null}
                {isEditable && isEditing && (
                    <Box>
                        <TextField
                            sx={{
                                marginTop: '4px',
                                width: '100%',
                                fontSize: '14px',
                                '& .MuiOutlinedInput-input': {
                                    padding: '8px 12px',
                                },
                            }}
                            value={draftValue}
                            onChange={handleInputChange}
                            error={!!localError}
                            helperText={localError}
                        />
                    </Box>
                )}
                {isCopyable && (
                    <IconButton onClick={() => navigator.clipboard.writeText(`${value}`)}>
                        <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                )}
                {isEditable && !isEditing && (
                    <IconButton onClick={startEditing}>
                        <EditOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                )}
                {isEditable && isEditing && (
                    <Box sx={{ display: 'flex', gap: '12px' }}>
                        <Tooltip arrow placement="top" title="Confirm" enterDelay={1000}>
                            <IconButton onClick={handleUpdate}>
                                <CheckRoundedIcon sx={{ color: theme.palette.primary.P_750 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip arrow placement="top" title="Discard" enterDelay={1000}>
                            <IconButton onClick={handleDiscard}>
                                <CloseOutlinedIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
